import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock } from 'lucide-react';
import { resetPasswordSchema, type ResetPasswordInput } from '@tutor/validation';
import { Alert, Button, Input } from '@tutor/ui';
import { ApiError, api } from '../../lib/api-client';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const token = searchParams.get('token') ?? '';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token, password: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.post('/api/auth/reset-password', values);
      navigate('/login', {
        replace: true,
        state: { notice: 'Your password has been reset. Please sign in.' },
      });
    } catch (error) {
      setFormError(
        error instanceof ApiError ? error.message : 'We could not reset your password. Please try again.',
      );
    }
  });

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <Alert tone="danger" title="This link is not valid">
            The reset link is missing or incomplete. Please request a new one.
          </Alert>
          <Link
            to="/forgot-password"
            className="mt-4 block text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Choose a new password</h1>
        <p className="mt-1.5 text-sm text-slate-500">
          Use at least 8 characters, with upper and lower case, a number and a symbol.
        </p>

        {formError && (
          <Alert tone="danger" className="mt-6">
            {formError}
          </Alert>
        )}

        <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
          <input type="hidden" {...register('token')} />

          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            leftIcon={<Lock className="h-4 w-4" aria-hidden />}
            error={errors.password?.message}
            {...register('password')}
          />

          <Input
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            leftIcon={<Lock className="h-4 w-4" aria-hidden />}
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <Button type="submit" fullWidth size="lg" isLoading={isSubmitting}>
            Reset password
          </Button>
        </form>
      </div>
    </div>
  );
}
